import { useAttachments } from '$src/lib/hooks/useAttachments'
import { maintenanceLogHooks } from '$src/lib/trpcHooks'
import { notifications } from '@mantine/notifications'
import { AppModal } from '@markku/components'
import { useState } from 'react'
import { SystemTaskDetails } from './SystemTaskDetails'
import { SystemTaskFormData } from './SystemTaskDetails/types'

import type { AttachmentResponse } from '@markku/types'

export interface SystemTaskDetailsData {
  id: string
  title: string
  description?: string
  category?: string
  estimatedDuration?: string
  isUserTask: boolean
  completed: boolean
  attachments?: AttachmentResponse[]
  log?: any
}

interface SystemTaskDetailsModalProps {
  onClose: () => void
  task: SystemTaskDetailsData
  propertyId: number
  hasPackage: boolean
  onSave?: () => void
}

export function SystemTaskDetailsModal({
  onClose,
  task,
  propertyId,
  hasPackage,
  onSave,
}: SystemTaskDetailsModalProps) {
  const [isSaving, setIsSaving] = useState(false)
  const [markAsCompleted, setMarkAsCompleted] = useState(
    !!task.log?.completedDate,
  )

  const [formData, setFormData] = useState<SystemTaskFormData>({
    notes: task.log?.notes || '',
    completedDate:
      task.log?.completedDate || new Date().toISOString().split('T')[0],
  })

  // Initialize attachments hook with log attachments
  const attachments = useAttachments('maintenance_log', task.log?.attachments)

  const { mutateAsync: createLog } = maintenanceLogHooks.useCreate()
  const { mutateAsync: updateLog } = maintenanceLogHooks.useUpdate()

  // Determine if this is a Markku task (not a user task)
  const isMarkkuTask = !task.isUserTask

  const validateForm = () => {
    if (markAsCompleted && !formData.completedDate) {
      notifications.show({
        title: 'Virhe',
        message:
          'Suorituspäivämäärä on pakollinen kun merkitset tehtävän tehdyksi',
        color: 'red',
      })
      return false
    }
    return true
  }

  const handleSave = async () => {
    if (!validateForm()) {
      return
    }

    setIsSaving(true)
    try {
      const taskId = parseInt(task.id)

      if (task.log?.id) {
        // Update existing log with attachments
        await updateLog({
          id: task.log.id,
          data: {
            notes: formData.notes,
            completedDate: markAsCompleted ? formData.completedDate : null,
          },
          attachments: attachments.getChanges(),
        })
      } else {
        // Create new log with attachments
        await createLog({
          data: {
            propertyId,
            maintenanceTaskId: taskId,
            taskNameSnapshot: task.title,
            descriptionSnapshot: task.description,
            completedDate: markAsCompleted ? formData.completedDate : null,
            // Note: completedByUserId is auto-populated by backend via contextFields
            notes: formData.notes || null,
          },
          attachments: attachments.getChanges(),
        })
      }

      notifications.show({
        title: 'Onnistui',
        message: markAsCompleted
          ? 'Tehtävä merkitty tehdyksi'
          : 'Muistiinpanot tallennettu',
        color: 'green',
      })

      onSave?.()
      onClose()
    } catch (error) {
      console.error('Error saving system task:', error)
      notifications.show({
        title: 'Virhe',
        message: 'Tallentaminen epäonnistui',
        color: 'red',
      })
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <AppModal
      opened={true}
      onClose={onClose}
      title="Hoidettava tehtävä"
      confirmProps={
        isMarkkuTask
          ? undefined
          : {
              label: 'Tallenna',
              onClick: handleSave,
              loading: isSaving,
            }
      }
      cancelProps={{
        label: 'Peruuta',
      }}
      showFooter={true}
    >
      <SystemTaskDetails
        task={task}
        formData={formData}
        onFormDataChange={setFormData}
        markAsCompleted={markAsCompleted}
        onMarkAsCompletedChange={setMarkAsCompleted}
        disabled={isSaving}
        hasPackage={hasPackage}
        attachments={attachments}
      />
    </AppModal>
  )
}
